// shell/components/entity-fields/view-renderers/entity-link-renderer.ts

import { createElement } from '../../../utils/dom';
import type { EntityLinkViewField } from '../../entity-view/types';
import type { PaneInstanceSpec } from '../../../core/types';

/**
 * Context for entity link navigation.
 */
export interface EntityLinkContext {
    /** Callback to open a pane */
    openPane: (spec: PaneInstanceSpec) => void;
}

// Module-level context (set by the EntityView when rendering)
let linkContext: EntityLinkContext | null = null;

/**
 * Set the link context for entity-link renderers.
 * Call this before rendering EntityView.
 */
export function setEntityLinkContext(ctx: EntityLinkContext | null): void {
    linkContext = ctx;
}

/**
 * Renders a clickable entity link that opens another pane.
 */
export function renderEntityLinkView<T>(
    field: EntityLinkViewField<T>,
    value: unknown,
    model: T
): HTMLElement {
    const container = createElement('div', {
        className: 'ev-field-view ev-field-view--entity-link',
    });

    if (value === null || value === undefined) {
        container.classList.add('ev-field-view--empty');
        container.textContent = '—';
        return container;
    }

    // Get display text from displayKey
    const displayValue = (model as Record<string, unknown>)[field.displayKey as string];
    const displayText = displayValue !== null && displayValue !== undefined
        ? String(displayValue)
        : String(value);

    const link = createElement('a', {
        className: 'ev-entity-link',
        textContent: displayText,
        attributes: {
            href: '#',
            role: 'button',
        },
        onClick: (e) => {
            e.preventDefault();
            if (linkContext) {
                const paneSpec = field.link(value, model);
                linkContext.openPane(paneSpec);
            }
        },
    });

    container.appendChild(link);
    return container;
}
